import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/16/solid';
import { Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import {
  INTERACTIVE_LIMITS,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import { useACLContext } from '../../../../../../../../providers/ACLProvider/ACL.hook';
import { LOCKED_FOOTER_TEXT } from '../../InteractiveAction.functions';
import InteractiveHeaderPicker from '../InteractiveHeaderPicker/InteractiveHeaderPicker.component';
import UTF16Counter from '../UTF16Counter/UTF16Counter.component';

// Header + footer live inside the composer. Body is sourced from the main
// chat text input (the "Escribe un mensaje…" textarea that already exists
// at the bottom of MessageBar) so sellers don't face two duplicate text
// entry points. The composer syncs `messageBarValue` into Formik's `body`
// field via effect — see InteractiveAction.component.tsx.
export default function CommonFields() {
  const { t } = useTranslation(['chat/chat']);
  const { values, handleChange, errors, touched, setFieldValue } =
    useFormikContext<tInteractiveOutbound>();
  const { canEditInteractiveFooter } = useACLContext();

  const bodyLimit =
    values.type === 'list'
      ? INTERACTIVE_LIMITS.LIST_BODY
      : INTERACTIVE_LIMITS.BUTTON_BODY;

  const bodyTouched = Boolean(touched.body);
  const bodyError = bodyTouched && errors.body ? String(errors.body) : null;

  // Enforce the locked footer on Pro/Basic plans. Runs on mount AND when
  // `canEditInteractiveFooter` flips (plan downgrade mid-session, or
  // swapping between sellers with different seat roles). Overrides any
  // custom footer that may have been loaded from a saved quick reply
  // created while the workspace was on Advanced — what the seller sees
  // must match what the recipient receives. bff-web's coerce is the
  // defense-in-depth layer; this is the UX layer.
  useEffect(() => {
    if (!canEditInteractiveFooter && values.footer !== LOCKED_FOOTER_TEXT) {
      setFieldValue('footer', LOCKED_FOOTER_TEXT);
    }
  }, [canEditInteractiveFooter, values.footer, setFieldValue]);

  return (
    <>
      <InteractiveHeaderPicker />

      <div className="InteractiveAction__BodyHint">
        <ChatBubbleBottomCenterTextIcon className="InteractiveAction__BodyHint__Icon" />
        <div className="InteractiveAction__BodyHint__Text">
          <p className="InteractiveAction__BodyHint__Title">
            {t('interactive.body.hintTitle')}
          </p>
          <p className="InteractiveAction__BodyHint__Subtitle">
            {t('interactive.body.hintSubtitle')}
          </p>
        </div>
        <UTF16Counter value={values.body} max={bodyLimit} />
      </div>
      {bodyError ? (
        <p className="InteractiveAction__Field__Error">{bodyError}</p>
      ) : null}

      <div className="InteractiveAction__Field">
        <label className="InteractiveAction__Field__Label">
          {t('interactive.footer.label')}
        </label>
        {/*
          DaisyUI Tooltip wraps the input so the upsell hint shows on hover
          of the wrapper, not the input itself. The native `title` attribute
          fails silently on `disabled` inputs in Chrome/Safari because those
          browsers suppress pointer events on disabled form controls — using
          a wrapper sidesteps that quirk. When canEditInteractiveFooter is
          true (Advanced / Trial) we skip the wrapper entirely so no
          `data-tip` attribute leaks into the DOM for paying customers.
        */}
        {canEditInteractiveFooter ? (
          <input
            type="text"
            name="footer"
            dir="auto"
            maxLength={INTERACTIVE_LIMITS.FOOTER}
            className="InteractiveAction__Field__Input"
            placeholder={t('interactive.footer.placeholder')}
            value={values.footer ?? ''}
            onChange={handleChange}
          />
        ) : (
          <Tooltip
            message={t('interactive.footer.lockedTooltip')}
            position="top"
            className="InteractiveAction__Field__Tooltip"
          >
            <input
              type="text"
              name="footer"
              dir="auto"
              maxLength={INTERACTIVE_LIMITS.FOOTER}
              className="InteractiveAction__Field__Input"
              placeholder={t('interactive.footer.placeholder')}
              value={values.footer ?? ''}
              onChange={handleChange}
              readOnly
              disabled
            />
          </Tooltip>
        )}
        <UTF16Counter
          value={values.footer}
          max={INTERACTIVE_LIMITS.FOOTER}
        />
      </div>
    </>
  );
}
